import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ProductImage from "@/components/ProductImage";
import { inr } from "@/lib/format";
import { SIZE_CHART_DATA, SIZE_GUIDE_INTRO, getSizeGuide } from "@/lib/sizeGuide";
import MeasureGuide from "@/components/MeasureGuide";

const SizeGuideModal = ({ open, onOpenChange, product, selectedSize, onSelectSize }) => {
  const [tab, setTab] = useState("chart");
  const [unit, setUnit] = useState("in");

  const categoryGuide = getSizeGuide(product?.category);
  const chart = SIZE_CHART_DATA[unit];

  useEffect(() => {
    if (open) setTab("chart");
  }, [open]);

  if (!product) return null;

  const image = product.images?.[0] || product.image;
  const unitLabel = chart.label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] max-w-[760px] gap-0 overflow-hidden border-border p-0 sm:rounded-none [&>button]:hidden"
        data-testid="size-guide-modal"
      >
        <div className="flex items-start gap-3 border-b border-border px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="mt-0.5 shrink-0 text-primary/70 hover:text-primary"
            aria-label="Close size guide"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 gap-3">
            <div className="h-16 w-12 shrink-0 overflow-hidden bg-muted">
              <ProductImage src={image} alt={product.name} className="h-full w-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary">Oori Label</p>
              <p className="line-clamp-2 text-sm font-medium text-primary">{product.name}</p>
              <p className="mt-1 text-sm text-primary">{inr(product.price)}</p>
            </div>
          </div>
        </div>

        <div className="flex border-b border-border">
          {[
            ["chart", "Size Chart"],
            ["howto", "How to measure"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`flex-1 border-b-2 px-4 py-3.5 text-sm font-semibold transition ${
                tab === id
                  ? "border-primary text-primary"
                  : "border-transparent text-secondary hover:text-primary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto px-4 py-5 sm:px-6">
          {tab === "chart" ? (
            <>
              <p className="mb-4 text-xs leading-relaxed text-primary/75">{SIZE_GUIDE_INTRO}</p>
              <p className="mb-4 text-xs leading-relaxed text-primary/75">
                <span className="font-medium text-primary">{categoryGuide.label}:</span> {categoryGuide.fit}
              </p>

              <div className="mb-4 flex justify-end">
                <div className="inline-flex rounded-full bg-muted p-0.5">
                  {["in", "cm"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setUnit(u)}
                      className={`min-w-[44px] rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                        unit === u ? "bg-primary text-primary-foreground" : "text-secondary"
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[680px] w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs font-semibold text-primary">
                      <th className="w-8 pb-3" />
                      <th className="pb-3 pr-3">Size</th>
                      <th className="pb-3 pr-3 whitespace-nowrap">Across Shoulder ({unitLabel})</th>
                      <th className="pb-3 pr-3 whitespace-nowrap">Bust ({unitLabel})</th>
                      <th className="pb-3 pr-3 whitespace-nowrap">Front Length ({unitLabel})</th>
                      <th className="pb-3 pr-3 whitespace-nowrap">Garment Waist ({unitLabel})</th>
                      <th className="pb-3 pr-3 whitespace-nowrap">Inseam ({unitLabel})</th>
                      <th className="pb-3 pr-3 whitespace-nowrap">To Fit Hip ({unitLabel})</th>
                      <th className="pb-3 whitespace-nowrap">To Fit Waist ({unitLabel})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chart.rows.map((row) => {
                      const available = product.sizes?.includes(row.size);
                      const selected = selectedSize === row.size;
                      return (
                        <tr
                          key={row.size}
                          onClick={() => available && onSelectSize?.(row.size)}
                          className={`border-b border-border/60 ${available ? "cursor-pointer hover:bg-muted/40" : "opacity-40"}`}
                          data-testid={`size-guide-row-${row.size}`}
                        >
                          <td className="py-3 pr-2">
                            <span
                              className={`inline-block h-[18px] w-[18px] rounded-full border ${
                                selected ? "border-primary" : "border-border"
                              }`}
                            >
                              {selected && (
                                <span className="m-[3px] block h-[9px] w-[9px] rounded-full bg-primary" />
                              )}
                            </span>
                          </td>
                          <td className="py-3 pr-3 font-semibold">{row.size}</td>
                          <td className="py-3 pr-3">{row.shoulder}</td>
                          <td className="py-3 pr-3">{row.bust}</td>
                          <td className="py-3 pr-3">{row.length}</td>
                          <td className="py-3 pr-3">{row.waist}</td>
                          <td className="py-3 pr-3">{row.inseam}</td>
                          <td className="py-3 pr-3">{row.hip}</td>
                          <td className="py-3">{row.toWaist}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="mt-4 text-xs text-secondary">
                * To Fit denotes body measurements. Garment columns are flat garment measurements.
              </p>
              <button
                type="button"
                onClick={() => setTab("howto")}
                className="mt-4 text-sm font-semibold text-primary underline underline-offset-4"
              >
                How to measure yourself
              </button>
            </>
          ) : (
            <div className="pb-2">
              <MeasureGuide />
              <button
                type="button"
                onClick={() => setTab("chart")}
                className="mt-6 text-sm font-semibold text-primary underline underline-offset-4"
              >
                Back to size chart
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SizeGuideModal;
